// =============================================================================
// Accountant Deal Page (Tax Portal)
// =============================================================================
// Shows investors and financials for a deal
// Flow: Dashboard -> Deal -> (Investors | Financials)
// =============================================================================

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserCircleIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, DocumentViewerModal } from '../../components/ui';
import { AuthenticatorModal } from '../../components/ui/AuthenticatorModal';
import {
  accountantService,
  hasTotpSession,
  setTotpSession,
  type AccountantDealInvestorsResponse,
  type AccountantDealInvestor,
  type AccountantDocument,
} from '../../services/accountant.service';

// =============================================================================
// Types
// =============================================================================

type TabType = 'investors' | 'financials';

// =============================================================================
// Component
// =============================================================================

export function AccountantDealPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('investors');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<AccountantDealInvestorsResponse | null>(null);
  const [financials, setFinancials] = useState<AccountantDocument[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() - 1);
  const [error, setError] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{ id: string; name: string } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showTotpModal, setShowTotpModal] = useState(false);
  const [pendingExport, setPendingExport] = useState(false);

  useEffect(() => {
    if (dealId) {
      loadDealInvestors();
      loadFinancials();
    }
  }, [dealId, selectedYear]);

  const loadDealInvestors = async () => {
    if (!dealId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await accountantService.getDealInvestors(dealId, selectedYear);
      setData(result);
    } catch (err) {
      console.error('Failed to load deal investors:', err);
      setError('Failed to load deal data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFinancials = async () => {
    if (!dealId) return;
    try {
      const docs = await accountantService.getDealFinancials(dealId, selectedYear);
      setFinancials(docs);
    } catch (err) {
      console.error('Failed to load financials:', err);
      // Don't set error - financials are optional
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProfileTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      LLC: 'LLC',
      TRUST: 'Trust',
      CORPORATION: 'Corporation',
      PARTNERSHIP: 'Partnership',
      IRA: 'IRA',
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const handleExportPartnerInfo = async () => {
    if (!dealId) return;

    // Check TOTP session before proceeding
    if (!hasTotpSession()) {
      setPendingExport(true);
      setShowTotpModal(true);
      return;
    }

    setIsExporting(true);
    try {
      await accountantService.exportPartnerInfo(dealId, selectedYear);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'TOTP_REQUIRED' || code === 'TOTP_NOT_SETUP') {
        setPendingExport(true);
        setShowTotpModal(true);
      } else {
        console.error('Failed to export partner info:', err);
        setError('Failed to export partner information. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleTotpVerified = () => {
    setTotpSession();
    setShowTotpModal(false);
    if (pendingExport) {
      setPendingExport(false);
      handleExportPartnerInfo();
    }
  };

  // Use available years from backend (based on deal acquisition date)
  const availableYears = data?.availableTaxYears || [new Date().getFullYear() - 1];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-24 bg-gray-200 rounded-xl" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <Link
          to="/accountant"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Deals
        </Link>
        <Card padding="lg">
          <div className="text-center py-12">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {error || 'Deal not found'}
            </h3>
            <Button className="mt-4" onClick={loadDealInvestors}>
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/accountant"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{data.deal.name}</h1>
              <p className="text-sm text-gray-500">
                {data.totalInvestors} investors - {data.deal.propertyType.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>

        {/* Tax Year Selector & Export */}
        <div className="flex items-center gap-3">
          <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                Tax Year {year}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPartnerInfo}
            disabled={isExporting || isLoading}
            className="flex items-center gap-2"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export Partner Info'}
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('investors')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'investors'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <UsersIcon className="h-5 w-5" />
          Investors
        </button>
        <button
          onClick={() => setActiveTab('financials')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'financials'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <CurrencyDollarIcon className="h-5 w-5" />
          Financials
        </button>
      </div>

      {/* Investors Tab Content */}
      {activeTab === 'investors' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card padding="md">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary-600">{data.totalInvestors}</p>
                <p className="text-sm text-gray-500 mt-1">Total Investors</p>
              </div>
            </Card>
            <Card padding="md">
              <div className="text-center">
                <p className="text-3xl font-bold text-success-600">{data.investorsWithK1}</p>
                <p className="text-sm text-gray-500 mt-1">K-1s Uploaded</p>
              </div>
            </Card>
            <Card padding="md">
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">{data.investorsWithoutK1}</p>
                <p className="text-sm text-gray-500 mt-1">Pending K-1s</p>
              </div>
            </Card>
          </div>

          {/* Investors List */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Investors - Tax Year {selectedYear}
            </h2>
            {data.investors.length === 0 ? (
              <Card padding="lg">
                <div className="text-center py-8">
                  <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No Investors</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    No investors found for this deal.
                  </p>
                </div>
              </Card>
            ) : (
              data.investors.map((investor: AccountantDealInvestor) => (
                <Link
                  key={investor.investmentId}
                  to={`/accountant/deals/${dealId}/investors/${investor.profileId}?taxYear=${selectedYear}`}
                >
                  <Card padding="md" hover className="transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${investor.hasK1 ? 'bg-success-100' : 'bg-amber-100'}`}>
                          {investor.type === 'INDIVIDUAL' ? (
                            <UserCircleIcon className={`h-6 w-6 ${investor.hasK1 ? 'text-success-600' : 'text-amber-600'}`} />
                          ) : (
                            <BuildingOfficeIcon className={`h-6 w-6 ${investor.hasK1 ? 'text-success-600' : 'text-amber-600'}`} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{investor.displayName}</h3>
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                              {getProfileTypeLabel(investor.type)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {investor.investorName} - {investor.investorEmail}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            <span className="text-gray-600">
                              Investment: <span className="font-medium">{formatCurrency(investor.investmentAmount)}</span>
                            </span>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-600">
                              Ownership: <span className="font-medium">{investor.ownershipPercentage.toFixed(2)}%</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {investor.hasK1 ? (
                          <div className="flex items-center gap-2 text-success-600">
                            <CheckCircleIcon className="h-5 w-5" />
                            <span className="text-sm font-medium">
                              {investor.k1Count} K-1{investor.k1Count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-amber-600">
                            <ExclamationCircleIcon className="h-5 w-5" />
                            <span className="text-sm font-medium">No K-1</span>
                          </div>
                        )}
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </Card>
                </Link>
              ))
            )}
          </div>

          {/* Quick Stats Footer */}
          {data.investors.length > 0 && (
            <Card padding="md" className="bg-gray-50">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">
                    Progress: {data.investorsWithK1} of {data.totalInvestors} K-1s uploaded for Tax Year {selectedYear}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-success-500 rounded-full transition-all"
                      style={{ width: `${(data.investorsWithK1 / data.totalInvestors) * 100}%` }}
                    />
                  </div>
                  <span className="font-medium text-gray-900">
                    {Math.round((data.investorsWithK1 / data.totalInvestors) * 100)}%
                  </span>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Financials Tab Content */}
      {activeTab === 'financials' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Financial Documents - Tax Year {selectedYear}
          </h2>

          {financials.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-8">
                <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Financial Documents</h3>
                <p className="mt-2 text-sm text-gray-500">
                  No financial documents have been uploaded for this deal for Tax Year {selectedYear}.
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {financials.map((doc) => (
                <Card
                  key={doc.id}
                  padding="md"
                  hover
                  className="cursor-pointer transition-all"
                  onClick={() => setViewingDocument({ id: doc.id, name: doc.name })}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{doc.name}</h3>
                        <p className="text-sm text-gray-500">
                          Uploaded {formatDate(doc.createdAt)}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          getEmbedUrls={accountantService.getDocumentViewUrl}
        />
      )}

      {/* TOTP Verification Modal */}
      <AuthenticatorModal
        isOpen={showTotpModal}
        onClose={() => {
          setShowTotpModal(false);
          setPendingExport(false);
        }}
        onVerified={handleTotpVerified}
        title="Verification Required"
        description="This export contains sensitive tax data including SSN and EIN. Please verify with your authenticator app to proceed."
      />
    </div>
  );
}

export default AccountantDealPage;
